import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import {
  createFrontendModule,
  createFrontendPlugin,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { ODOO_BRANCH_PATH, odooBranchRouteRef } from './branchRouteRef';
import { odooEnvironmentsFrTranslation } from './translation';

// A card is not attached to a given page: it declares a condition, and shows up
// on every entity that satisfies it.
const odooEnvironmentsEntityCard = EntityCardBlueprint.make({
  name: 'odoo-environments',
  params: {
    type: 'content',
    filter: {
      'metadata.annotations.odoo/project-id': { $exists: true },
    },
    loader: async () => {
      const { OdooEnvironmentsCard } = await import('./OdooEnvironmentsCard');
      return <OdooEnvironmentsCard />;
    },
  },
});

// The card must stay on the `catalog` plugin: that is the plugin owning the
// entity page it attaches to.
export default createFrontendModule({
  pluginId: 'catalog',
  extensions: [odooEnvironmentsEntityCard],
});

// The translation must go in a separate module on the `app` plugin:
// TranslationBlueprint is restricted to the app plugin, and an extension it
// creates under any other pluginId is ignored without warning or error — the
// card then renders in its default (English) messages. Do not merge these two
// modules back into one.
// Registered in packages/app/src/App.tsx alongside the card module.
export const odooEnvironmentsTranslationModule = createFrontendModule({
  pluginId: 'app',
  extensions: [odooEnvironmentsFrTranslation],
});

const odooBranchPage = PageBlueprint.make({
  name: 'branch',
  params: {
    // The two path segments are the two route-ref params, in order, and the
    // resolver percent-encodes each of them — which is why every link to this
    // page goes through the ref rather than composing the path by hand.
    //
    // The literal itself lives next to the ref: the sidebar matches the current
    // location against the same constant to tell which project is on screen.
    path: ODOO_BRANCH_PATH,
    // The page paints its own header, which carries the project and the branch
    // — the default plugin header above it would only add the word "Odoo" and a
    // second horizontal rule. Same choice the catalog makes for its entity
    // page.
    noHeader: true,
    routeRef: odooBranchRouteRef,
    loader: async () => {
      const { BranchPage } = await import('./BranchPage');
      return <BranchPage />;
    },
  },
});

/**
 * A third feature, and a plugin rather than a module.
 *
 * Not a module on `catalog`, which is where the card lives: a page is not part
 * of the catalog plugin, and mounting it there would quietly answer a product
 * question that is explicitly still open — whether the per-branch console lives
 * inside the catalog entity page or beside it (decision D8 in
 * ODOO_IDP_DEMO_DECISIONS.md defers exactly that arbitration). Its own plugin
 * keeps both futures available: the page can gain a nav entry, or be folded
 * into the entity page as a tab, without either move being pre-decided here.
 *
 * NO NAV ITEM, DELIBERATELY. The page is reached from the environments card of
 * a project, and it needs a project and a branch to mean anything — a sidebar
 * entry has neither, so it could only land on a page that cannot load. This is
 * a decision, not an oversight: do not "fix" it by adding a NavItemBlueprint.
 *
 * Registered in packages/app/src/App.tsx alongside the two modules above.
 */
export const odooBranchPagePlugin = createFrontendPlugin({
  pluginId: 'odoo',
  extensions: [odooBranchPage],
});
